import * as Sentry from '@sentry/react-native';
import React, { Component, ReactNode } from 'react';
import { TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import { useCrashlytics } from '@hooks/useCrashlytics';

interface Props {
  children: ReactNode;
  // A render function receives `reset`, so the fallback can offer a way back
  // instead of a dead end (TA-11053).
  fallback?: ReactNode | ((reset: () => void) => ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundaryClass extends Component<
  Props & { crashlytics: ReturnType<typeof useCrashlytics> },
  State
> {
  constructor(
    props: Props & { crashlytics: ReturnType<typeof useCrashlytics> }
  ) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      // A string, not a boolean: booleans serialize as "True", which makes
      // the tag unsearchable with errorBoundary:true in Sentry (TA-10454).
      tags: { errorBoundary: 'true' },
      extra: { componentStack: errorInfo.componentStack ?? undefined },
    });

    this.props.crashlytics.recordError(error);

    this.props.onError?.(error, errorInfo);
  }

  reset = () => this.setState({ hasError: false, error: undefined });

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === 'function') {
        return fallback(this.reset);
      }
      if (fallback) {
        return fallback;
      }

      return (
        <Box flex={1} justifyContent='center' alignItems='center' padding='l'>
          <Text variant='headerLabel' textAlign='center' marginBottom='m'>
            Something went wrong
          </Text>
          <Text variant='bodySecondary' textAlign='center' marginBottom='l'>
            We've been notified of this error and are working to fix it.
          </Text>
          <TouchableOpacity onPress={this.reset}>
            <Box backgroundColor='greenSecondary' padding='m' borderRadius='xs'>
              <Text variant='labelEmphasized' color='white'>
                Try Again
              </Text>
            </Box>
          </TouchableOpacity>
        </Box>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<Props> = (props) => {
  const crashlytics = useCrashlytics();
  return <ErrorBoundaryClass {...props} crashlytics={crashlytics} />;
};
